import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, Clock, AlertTriangle, Activity } from 'lucide-react';

interface RoutineEvent {
  time: string;
  activity: string;
  location: string;
  duration: number;
  adherence: number;
  status: 'COMPLETED' | 'MISSED' | 'DELAYED' | 'IN_PROGRESS';
}

interface DailyMetrics {
  routineAdherence: number;
  completedActivities: number;
  missedActivities: number;
  averageDelay: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
}

const DailyRoutine: React.FC = () => {
  // Mock data - in real app this would come from floor sensors and activity patterns
  const routineEvents: RoutineEvent[] = [
    { time: '7:00 AM', activity: 'Wake Up', location: 'Bedroom', duration: 15, adherence: 95, status: 'COMPLETED' },
    { time: '7:30 AM', activity: 'Morning Medication', location: 'Kitchen', duration: 5, adherence: 100, status: 'COMPLETED' },
    { time: '8:00 AM', activity: 'Breakfast', location: 'Kitchen', duration: 25, adherence: 90, status: 'COMPLETED' },
    { time: '9:00 AM', activity: 'Light Exercise', location: 'Living Room', duration: 20, adherence: 85, status: 'DELAYED' },
    { time: '12:00 PM', activity: 'Lunch', location: 'Kitchen', duration: 30, adherence: 92, status: 'IN_PROGRESS' },
    { time: '2:00 PM', activity: 'Social Activity', location: 'Living Room', duration: 60, adherence: 75, status: 'MISSED' },
    { time: '5:00 PM', activity: 'Evening Medication', location: 'Kitchen', duration: 5, adherence: 98, status: 'COMPLETED' },
    { time: '6:00 PM', activity: 'Dinner', location: 'Kitchen', duration: 30, adherence: 88, status: 'COMPLETED' },
    { time: '9:00 PM', activity: 'Bedtime Routine', location: 'Bedroom', duration: 20, adherence: 82, status: 'COMPLETED' }
  ];

  const hourlyActivity = [
    { hour: '6 AM', activity: 10 },
    { hour: '7 AM', activity: 45 },
    { hour: '8 AM', activity: 65 },
    { hour: '9 AM', activity: 40 },
    { hour: '10 AM', activity: 30 },
    { hour: '11 AM', activity: 25 },
    { hour: '12 PM', activity: 55 },
    { hour: '1 PM', activity: 35 },
    { hour: '2 PM', activity: 20 },
    { hour: '3 PM', activity: 25 },
    { hour: '4 PM', activity: 30 },
    { hour: '5 PM', activity: 45 },
    { hour: '6 PM', activity: 50 },
    { hour: '7 PM', activity: 35 },
    { hour: '8 PM', activity: 25 },
    { hour: '9 PM', activity: 15 }
  ];

  const metrics: DailyMetrics = {
    routineAdherence: 88,
    completedActivities: 6,
    missedActivities: 1,
    averageDelay: 12,
    riskLevel: 'LOW'
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'text-green-500';
      case 'MISSED':
        return 'text-red-500';
      case 'DELAYED':
        return 'text-yellow-500';
      case 'IN_PROGRESS':
        return 'text-blue-500';
      default:
        return 'text-gray-500';
    }
  };

  const getAdherenceColor = (adherence: number) => {
    if (adherence >= 90) return 'text-green-500';
    if (adherence >= 75) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Daily Routine Monitor</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Calendar className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Routine Adherence</h3>
          </div>
          <p className={`text-3xl font-bold ${getAdherenceColor(metrics.routineAdherence)}`}>
            {metrics.routineAdherence}%
          </p>
          <p className="text-sm text-gray-500">Daily average</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Activity className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Completed Activities</h3>
          </div>
          <p className="text-3xl font-bold">{metrics.completedActivities}</p>
          <p className="text-sm text-gray-500">Out of {metrics.completedActivities + metrics.missedActivities}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Clock className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Average Delay</h3>
          </div>
          <p className="text-3xl font-bold">{metrics.averageDelay}min</p>
          <p className="text-sm text-gray-500">From scheduled time</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <AlertTriangle className="text-orange-500 mr-2" />
            <h3 className="text-lg font-semibold">Risk Level</h3>
          </div>
          <p className={`text-3xl font-bold ${getStatusColor(metrics.riskLevel)}`}>
            {metrics.riskLevel}
          </p>
          <p className="text-sm text-gray-500">Based on adherence</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Daily Activity Timeline</h3>
          <div className="space-y-4">
            {routineEvents.map((event, index) => (
              <div key={index} className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0 w-20">
                  <span className="font-medium">{event.time}</span>
                </div>
                <div className="flex-grow">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{event.activity}</span>
                    <span className={`text-sm ${getStatusColor(event.status)}`}>
                      {event.status.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Location: {event.location} • Duration: {event.duration}min
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Activity Level by Hour</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={hourlyActivity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="activity" name="Activity Level" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Routine Insights</h3>
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Calendar className="text-blue-500" />
            </div>
            <div>
              <h4 className="font-medium">Morning Routine</h4>
              <p className="text-gray-600">Consistent morning medication and breakfast schedule shows good adherence to health routines.</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <AlertTriangle className="text-yellow-500" />
            </div>
            <div>
              <h4 className="font-medium">Missed Activities</h4>
              <p className="text-gray-600">Social activity was missed today. Consider reviewing schedule or providing reminders.</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Activity className="text-green-500" />
            </div>
            <div>
              <h4 className="font-medium">Activity Patterns</h4>
              <p className="text-gray-600">Peak activity levels observed during morning hours. Consider redistributing activities throughout the day.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyRoutine; 